import Image from 'next/image'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'

import { Balance } from '@/api/models/finance'
import { ScreenSize } from '@/app/constants/index'
import { YandexMetrikaEvent } from '@/app/constants/metrics'
import { useCurrentBreakpoint } from '@/app/hooks/useCurrentBreakpoint'
import { cn } from '@/app/utils'
import { ALL_FILTER } from '@/components/pages/bill/ui/Bills.constants'
import { AppButton } from '@/components/shared/button'
import { useYandexMetrika } from '@/components/shared/yandex-metrika/hooks/useYandexMetrika'
import { formatRuble } from '@/utils/number'

import { useBalancePremiseOptions } from '../../hooks/useBalancePremiseOptions'
import { getBalancePremiseText } from '../../utils/getBalancePremiseText'
import { shouldShowInvoices } from '../../utils/shouldShowInvoices'

interface ISCBalanceSummaryProps {
  balance: Balance
}

const SCBalanceSummary = ({ balance }: ISCBalanceSummaryProps) => {
  const searchParams = useSearchParams()
  const { breakpoint } = useCurrentBreakpoint()
  const isTabletScreen = [ScreenSize.Md, ScreenSize.Lg].includes(breakpoint)
  const isMobileScreen = [
    ScreenSize.Xxs,
    ScreenSize.Xs,
    ScreenSize.Sm
  ].includes(breakpoint)
  const { getSelectedPremise } = useBalancePremiseOptions()
  const selectedPremise = getSelectedPremise()
  const { reachGoal } = useYandexMetrika()

  const onButtonClick = () => {
    if (balance.debtSum < 0) {
      reachGoal(YandexMetrikaEvent.MAIN_PAY_INVOICES)
    } else {
      reachGoal(YandexMetrikaEvent.MAIN_PAY_BALANCES)
    }
  }

  const createUrl = () => {
    const params = new URLSearchParams(searchParams?.toString())

    if (selectedPremise?.key) {
      params.set('drawer', balance.alias)
      params.set('accountId', selectedPremise?.key)
    } else {
      params.set('drawer', balance.alias)
      params.set('tab', ALL_FILTER.key)
    }

    return `/invoices?${params.toString()}`
  }

  return (
    <div
      className={cn(
        'relative w-full overflow-hidden rounded-16 bg-white p-20 transition-[height] duration-300 ease-in-out lg:p-16'
      )}
    >
      <div className="flex justify-between xs:pb-8">
        <div
          className={cn(
            'flex flex-col',
            (isMobileScreen || isTabletScreen) && 'w-full'
          )}
        >
          <Image
            src={balance.iconUrl ?? ''}
            alt="image"
            width={isMobileScreen || isTabletScreen ? 60 : 80}
            height={isMobileScreen || isTabletScreen ? 60 : 80}
          />
          <p className="truncate font-bold text-18 lg:text-16 xs:w-[170px]">
            {balance.name}
          </p>
          <p
            className={cn(
              'font-bold text-28 lg:text-24',
              balance.debtSum < 0 && '!text-bronze-40'
            )}
          >
            {formatRuble(Math.abs(balance.debtSum))}
          </p>
          <p className="mt-12 text-grey-70 text-12 lg:mt-4">
            {getBalancePremiseText(balance, selectedPremise)}
          </p>
          <Link href={createUrl()}>
            <AppButton
              className={cn(
                'mt-12 max-w-[190px] whitespace-nowrap',
                (isMobileScreen || isTabletScreen) && 'w-full max-w-full'
              )}
              variant="secondary"
              size="medium"
              onClick={onButtonClick}
            >
              {shouldShowInvoices(balance)
                ? 'Счета к оплате'
                : 'Пополнение баланса'}
            </AppButton>
          </Link>
        </div>
      </div>
    </div>
  )
}

export { SCBalanceSummary }
